import * as React from 'react'
import { Loader2 } from 'lucide-react'
import type { BillingRefundDto } from '@lib/dto/billing.dto'
import { BILLING } from '@lib/constants/billing'
import { formatCents, microsToWholeCents, parseDollarsToCents, refundQuote } from '@lib/helpers/money'
import { createRefundInputSchema } from '@lib/input/billing.input'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Field, fieldMessageId } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { toast } from '@/components/ui/toast'
import { apiErrorMessage } from '@/helpers/apiErrorMessage'
import { useBillingActions } from '@/hooks/useBilling'

/** Why an amount typed in dollars can't be refunded, or null when it can. */
function amountProblem(cents: number | null, refundableCents: number): string | null {
  if (cents === null || cents < 1) return 'Enter an amount like 10 or 10.00.'
  if (cents > refundableCents) return `You can refund at most ${formatCents(refundableCents)}.`
  return null
}

/** Says where the money is going; a refund Stripe couldn't fully make is followed up by PixelSign. */
function announce(refund: BillingRefundDto): void {
  if (refund.status === 'needs_attention') {
    toast(`Refunded ${formatCents(refund.netCents)}. Stripe couldn't return part of it to your card, so PixelSign will contact you.`)
    return
  }
  toast.success(`Refunding ${formatCents(refund.netCents)} to your card. It can take 5–10 business days to show.`)
}

interface RefundFormProps {
  refundableCents: number
  onDone: () => void
}

/**
 * Mounted each time the dialog opens: it starts at the full refundable amount
 * with a new request id, so a retried submit refunds once.
 */
function RefundForm({ refundableCents, onDone }: RefundFormProps) {
  const { createRefund } = useBillingActions()
  const [amount, setAmount] = React.useState(() => (refundableCents / 100).toFixed(2))
  const [requestId] = React.useState(() => crypto.randomUUID())
  const [error, setError] = React.useState<string | null>(null)
  const [formError, setFormError] = React.useState<string | null>(null)
  const cents = parseDollarsToCents(amount)
  const quote = cents !== null && amountProblem(cents, refundableCents) === null ? refundQuote(cents) : null

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault()
    const problem = amountProblem(cents, refundableCents)
    const parsed = createRefundInputSchema.safeParse({ amountCents: cents ?? Number.NaN, requestId })
    if (problem !== null || !parsed.success) {
      setError(problem ?? 'Enter an amount like 10 or 10.00.')
      return
    }
    setError(null)
    setFormError(null)
    try {
      announce(await createRefund.mutateAsync(parsed.data))
      onDone()
    } catch (failure) {
      setFormError(apiErrorMessage(failure, 'Could not refund the credit.'))
    }
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-4">
      <DialogHeader>
        <DialogTitle>Refund credit</DialogTitle>
        <DialogDescription>
          Takes unused paid credit off your balance and returns it, less a {BILLING.refundFeePercent}% fee.
        </DialogDescription>
      </DialogHeader>
      <Field label="Amount" htmlFor="refund-amount" error={error} hint={`Up to ${formatCents(refundableCents)}: paid credit not yet spent.`}>
        <Input
          id="refund-amount"
          inputMode="decimal"
          value={amount}
          aria-invalid={error ? true : undefined}
          aria-describedby={fieldMessageId('refund-amount')}
          onChange={(event) => setAmount(event.target.value)}
        />
      </Field>
      {quote ? (
        <dl aria-label="Refund breakdown" className="grid grid-cols-[7rem_minmax(0,1fr)] gap-x-3 gap-y-1 text-body tabular-nums">
          <dt className="text-muted-foreground">Refund</dt>
          <dd>{formatCents(quote.amountCents)}</dd>
          <dt className="text-muted-foreground">Fee ({BILLING.refundFeePercent}%)</dt>
          <dd>{formatCents(quote.feeCents)}</dd>
          <dt className="text-muted-foreground">You receive</dt>
          <dd>{formatCents(quote.netCents)} plus the sales tax paid on it</dd>
        </dl>
      ) : null}
      <p className="text-caption text-muted-foreground">
        Refunds go back to your card payments, newest first. Granted credit can&apos;t be refunded.
      </p>
      {formError ? <p className="text-body text-danger">{formError}</p> : null}
      <DialogFooter>
        <Button type="button" variant="secondary" onClick={onDone}>
          Cancel
        </Button>
        <Button type="submit" disabled={createRefund.isPending}>
          {createRefund.isPending ? <Loader2 className="animate-spin" /> : null}
          {quote ? `Refund ${formatCents(quote.netCents)}` : 'Refund'}
        </Button>
      </DialogFooter>
    </form>
  )
}

interface RefundDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  refundableMicros: number
}

/** An Owner refunds unused paid credit, seeing the fee and what comes back before confirming. */
export function RefundDialog({ open, onOpenChange, refundableMicros }: RefundDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <RefundForm refundableCents={microsToWholeCents(refundableMicros)} onDone={() => onOpenChange(false)} />
      </DialogContent>
    </Dialog>
  )
}
